import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Loader2, X, Camera, Upload, SkipForward, CheckCircle2, AlertTriangle,
  ShieldCheck, ChevronRight, ChevronLeft, Circle, Plane, Gauge, Video, MapPin, ClipboardList,
} from "lucide-react";

const ScoreRing = ({ value, label }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  const color = v >= 80 ? "#10b981" : v >= 55 ? "#f59e0b" : "#ef4444";
  return (
    <div className="flex flex-col items-center" data-testid="crcp-score-ring">
      <div className="relative h-20 w-20">
        <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#1e293b" strokeWidth="3" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3"
            strokeDasharray={`${v}, 100`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-extrabold text-white" data-testid="crcp-overall-score">{v}</span>
        </div>
      </div>
      <span className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">{label}</span>
    </div>
  );
};

function ItemRow({ item, capture, geo, dispatchId, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const status = capture?.status;
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("item_id", item.item_id);
    fd.append("file", file);
    if (geo?.lat) fd.append("lat", String(geo.lat));
    if (geo?.lng) fd.append("lng", String(geo.lng));
    try {
      const { data } = await api.post(`/dispatches/${dispatchId}/crcp/capture`, fd);
      (data.warnings || []).forEach((w) => toast.warning(w));
      if (!data.warnings?.length) toast.success(`Captured: ${item.label}`);
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const skip = async () => {
    const reason = window.prompt(`Reason for skipping "${item.label}"? (e.g. not applicable, unsafe)`);
    if (reason === null) return;
    try {
      await api.post(`/dispatches/${dispatchId}/crcp/skip`, { item_id: item.item_id, reason: reason || "Not applicable" });
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const isVideo = item.media === "video";
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/50 px-3 py-2.5"
      data-testid={`crcp-item-${item.item_id}`}>
      <div className="flex items-center gap-2 min-w-0">
        {status === "captured" ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          : status === "skipped" ? <SkipForward className="w-4 h-4 text-slate-500 shrink-0" />
            : <Circle className={`w-4 h-4 shrink-0 ${item.required ? "text-amber-400" : "text-slate-600"}`} />}
        <div className="min-w-0">
          <div className="text-sm text-slate-100 truncate">{item.label}
            {item.required && <span className="ml-1.5 text-[10px] uppercase text-amber-400">req</span>}</div>
          {capture?.duplicate && <div className="text-[11px] text-amber-400">Possible duplicate</div>}
          {status === "skipped" && capture?.skip_reason && <div className="text-[11px] text-slate-500">Skipped · {capture.skip_reason}</div>}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <input ref={inputRef} type="file" accept={isVideo ? "video/*" : "image/*"}
          capture={isVideo ? "environment" : "environment"} className="hidden"
          onChange={(e) => upload(e.target.files?.[0])} data-testid={`crcp-file-${item.item_id}`} />
        <button onClick={() => inputRef.current?.click()} disabled={busy}
          data-testid={`crcp-capture-${item.item_id}`}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-cyan-500 text-slate-950 text-xs font-semibold hover:bg-cyan-400 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isVideo ? <Video className="w-3.5 h-3.5" /> : <Camera className="w-3.5 h-3.5" />}
          {status === "captured" ? "Retake" : "Capture"}
        </button>
        {status !== "captured" && (
          <button onClick={skip} data-testid={`crcp-skip-${item.item_id}`}
            className="p-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-400 hover:text-slate-200" title="Skip">
            <SkipForward className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

const ENV_LABELS = {
  weather: "Weather conditions", time_arrived: "Time arrived on scene", time_completed: "Time completed",
  gps_coordinates: "GPS coordinates", compass_orientation: "Compass orientation", num_lanes: "Number of lanes",
  lane_width: "Approx. lane width", notes: "Additional notes",
};

export default function CrcpCapture({ dispatchId, onClose, onCompleted }) {
  const [session, setSession] = useState(undefined);
  const [live, setLive] = useState({ score: null, ready: false });
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [geo, setGeo] = useState(null);
  const [env, setEnv] = useState({});
  const [completing, setCompleting] = useState(false);
  const [requests, setRequests] = useState([]);

  const refresh = useCallback(async () => {
    const { data } = await api.get(`/dispatches/${dispatchId}/crcp`);
    if (data.session) {
      setSession(data.session);
      setEnv(data.session.environmental || {});
      setLive({ score: data.live_score, qc: data.live_qc, ready: data.ready_to_complete });
    }
    try { const r = await api.get(`/dispatches/${dispatchId}/evidence-requests`); setRequests(r.data || []); } catch { /* ignore */ }
  }, [dispatchId]);

  useEffect(() => {
    (async () => {
      try {
        await api.post(`/dispatches/${dispatchId}/crcp/begin`);
        await refresh();
      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); onClose(); }
    })();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setGeo({ lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) }),
        () => {}, { enableHighAccuracy: true, timeout: 8000 });
    }
    // eslint-disable-next-line
  }, [dispatchId]);

  const saveEnv = async () => {
    try { await api.put(`/dispatches/${dispatchId}/crcp/environmental`, env); toast.success("Environmental info saved"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const complete = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`/dispatches/${dispatchId}/crcp/complete`, {});
      toast.success(`Evidence package delivered — Score ${data.score.evidence_capture_score}/100`);
      onCompleted?.(data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing_items) {
        toast.error(`${detail.message} Missing: ${detail.missing_items.slice(0, 4).join(", ")}${detail.missing_items.length > 4 ? "…" : ""}`);
      } else toast.error(formatApiErrorDetail(detail));
    } finally { setCompleting(false); }
  };

  if (session === undefined)
    return (
      <div className="fixed inset-0 z-[60] bg-navy/95 flex items-center justify-center" data-testid="crcp-loading">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );

  const phases = session.checklist?.phases || [];
  const phase = phases[phaseIdx];
  const captures = session.captures || {};
  const score = live.score || {};
  const missing = score.missing_items || [];

  const flags = session.checklist?.complexity?.flags || [];

  return (
    <div className="fixed inset-0 z-[60] bg-navy text-slate-100 overflow-y-auto" data-testid="crcp-workflow">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-navy/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-cyan-400 uppercase tracking-widest flex items-center gap-1.5">
              <Plane className="w-3.5 h-3.5" /> Crash Ready Capture Protocol™</p>
            <h2 className="font-heading text-lg font-extrabold text-white truncate">{session.case_number || "Evidence Capture"}</h2>
          </div>
          <button onClick={onClose} data-testid="crcp-close" className="p-2 rounded-full bg-slate-800 hover:bg-slate-700"><X className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5">
        {/* Attorney-requested additional evidence */}
        {requests.length > 0 && (
          <div className="mb-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="crcp-requests-banner">
            <div className="text-sm font-semibold text-amber-300 flex items-center gap-1.5"><ClipboardList className="w-4 h-4" /> Additional evidence requested</div>
            {requests.map((r) => (
              <div key={r.request_id} className="mt-1.5 text-xs text-amber-100/90">
                <span className="uppercase font-semibold text-amber-300">{r.priority}</span> · {r.attorney_name || "Attorney"}: {r.items.filter((i) => !(r.fulfilled_items || []).includes(i.item_id)).map((i) => i.label).join(", ") || "all items completed"}
                {r.note && <span className="block text-amber-200/70 italic">“{r.note}”</span>}
              </div>
            ))}
          </div>
        )}

        {/* Score dashboard */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 flex items-center justify-between flex-wrap gap-4" data-testid="crcp-score-panel">
          <ScoreRing value={score.evidence_capture_score} label="Capture Score™" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-center flex-1">
            {[["Court Ready", score.court_readiness], ["Reconstruction", score.crash_reconstruction_readiness],
              ["Completeness", score.evidence_completeness], ["Image Quality", score.image_quality]].map(([l, v]) => (
              <div key={l}><div className="text-lg font-bold text-white" data-testid={`crcp-sub-${l.replace(/\s/g, "-").toLowerCase()}`}>{v ?? 0}%</div><div className="text-[10px] uppercase tracking-wide text-slate-400">{l}</div></div>
            ))}
          </div>
        </div>
        {flags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="crcp-flags">
            {flags.map((f) => <span key={f} className="text-[11px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 capitalize">{f.replace(/_/g, " ")}</span>)}
          </div>
        )}

        {/* Phase stepper */}
        <div className="mt-5 flex items-center gap-1 overflow-x-auto pb-1" data-testid="crcp-phase-stepper">
          {phases.map((p, i) => (
            <button key={p.key} onClick={() => setPhaseIdx(i)} data-testid={`crcp-phase-tab-${p.key}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${i === phaseIdx ? "bg-cyan-500 text-slate-950" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
              {i + 1}. {p.name}
            </button>
          ))}
        </div>

        {/* Phase body */}
        <div className="mt-4">
          <h3 className="font-heading font-bold text-white flex items-center gap-2"><MapPin className="w-4 h-4 text-cyan-400" /> {phase.name}</h3>

          {phase.key === "environmental" ? (
            <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="crcp-env-form">
              {(phase.fields || []).map((f) => (
                <label key={f} className={f === "notes" ? "sm:col-span-2" : ""}>
                  <span className="text-xs text-slate-400">{ENV_LABELS[f] || f}</span>
                  {f === "notes"
                    ? <textarea rows={2} value={env[f] || ""} onChange={(e) => setEnv({ ...env, [f]: e.target.value })}
                        data-testid={`crcp-env-${f}`} className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-500" />
                    : <input value={env[f] || ""} onChange={(e) => setEnv({ ...env, [f]: e.target.value })}
                        data-testid={`crcp-env-${f}`} className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-500" />}
                </label>
              ))}
              <button onClick={saveEnv} data-testid="crcp-env-save" className="sm:col-span-2 flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400"><ShieldCheck className="w-4 h-4" /> Save environmental info</button>
            </div>
          ) : phase.vehicles ? (
            <div className="mt-3 space-y-5">
              {phase.vehicles.map((v) => (
                <div key={v.vehicle_index} data-testid={`crcp-vehicle-${v.vehicle_index}`}>
                  <div className="text-xs font-semibold text-cyan-300 uppercase tracking-wide mb-2">Vehicle {v.vehicle_index}</div>
                  <div className="space-y-2">
                    {v.items.map((it) => <ItemRow key={it.item_id} item={it} capture={captures[it.item_id]} geo={geo} dispatchId={dispatchId} onChange={refresh} />)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {(phase.items || []).map((it) => <ItemRow key={it.item_id} item={it} capture={captures[it.item_id]} geo={geo} dispatchId={dispatchId} onChange={refresh} />)}
            </div>
          )}
        </div>

        {/* Nav + complete */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <button disabled={phaseIdx === 0} onClick={() => setPhaseIdx((i) => Math.max(0, i - 1))}
            className="flex items-center gap-1 px-4 py-2 rounded-full bg-slate-800 text-slate-300 text-sm disabled:opacity-40"><ChevronLeft className="w-4 h-4" /> Back</button>
          {phaseIdx < phases.length - 1
            ? <button onClick={() => setPhaseIdx((i) => Math.min(phases.length - 1, i + 1))} data-testid="crcp-next-phase"
                className="flex items-center gap-1 px-5 py-2 rounded-full bg-navy border border-cyan-500/40 text-cyan-300 text-sm font-semibold">Next <ChevronRight className="w-4 h-4" /></button>
            : <span className="text-xs text-slate-500">Final phase</span>}
        </div>

        {/* Completion */}
        <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4" data-testid="crcp-completion">
          {live.ready
            ? <div className="flex items-center gap-2 text-emerald-300 text-sm"><CheckCircle2 className="w-5 h-5" /> All required evidence captured. Ready to deliver the package.</div>
            : <div className="flex items-start gap-2 text-amber-300 text-sm">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <div>Incomplete — {missing.length} required item{missing.length === 1 ? "" : "s"} remaining{missing.length ? `: ${missing.slice(0, 5).join(", ")}${missing.length > 5 ? "…" : ""}` : ""}.
                {!live.qc?.video_present && <div className="text-amber-400 mt-0.5">Required scene video not captured yet.</div>}</div>
              </div>}
          <button onClick={complete} disabled={!live.ready || completing} data-testid="crcp-complete"
            className="mt-3 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed">
            {completing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Gauge className="w-5 h-5" />} Complete & deliver evidence package
          </button>
        </div>
      </div>
    </div>
  );
}
